import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Navigate, useParams } from 'react-router-dom';
import './index.css';
import './styles/themes.css';

// Security and initialization
import { disableConsoleInProduction } from './utils/disableConsole';
import { initializeSecurity } from './utils/security';
import { cleanupCorruptedAuth, cleanupOldTokens } from './utils/cleanupAuth';

disableConsoleInProduction();
initializeSecurity();

if (import.meta.env.DEV) {
  console.log('[DEBUG] Iniciando limpeza de autenticação...');
}
cleanupCorruptedAuth();
cleanupOldTokens();
if (import.meta.env.DEV) {
  console.log('[DEBUG] Limpeza de autenticação concluída');
}

// Global error handlers
window.addEventListener('error', (event) => {
  if (import.meta.env.PROD) {
    // TODO: Sentry integration
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (import.meta.env.PROD) {
    // TODO: Sentry integration
  }
});

// Critical imports (loaded immediately)
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import RootLayout from './components/RootLayout';
import ProtectedRoute from './components/ProtectedRoute';
import BusinessOwnerProtectedRoute from './components/BusinessOwnerProtectedRoute';
import OnboardingMiddleware from './middleware/onboardingMiddleware';

// Loading component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#111827',
    color: '#C9A961'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '50px',
        height: '50px',
        border: '3px solid #C9A961',
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 20px'
      }}></div>
      <p>Carregando...</p>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  </div>
);

// Lazy-loaded pages - Public pages (high priority)
const LandingPage = lazy(() => import('./pages/Landing'));
const SectorLanding = lazy(() => import('./pages/SectorLanding'));
const LoginPage = lazy(() => import('./pages/Login'));
const RegisterPage = lazy(() => import('./pages/Register'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'));

// Lazy-loaded pages - Busca pública
const BuscarPage = lazy(() => import('./pages/Buscar'));

// Lazy-loaded pages - Prontuários (apenas saúde)
const ProntuariosPage = lazy(() => import('./pages/Prontuarios'));
const ProntuarioDetailPage = lazy(() => import('./pages/Prontuarios/Detail'));

// Lazy-loaded pages - Fichas de Beleza (setor beleza)
const FichasBelezaPage = lazy(() => import('./pages/FichasBeleza'));
const FichaBelezaDetailPage = lazy(() => import('./pages/FichasBeleza/Detail'));

// Lazy-loaded pages - Fitness (setor fitness)
const FitnessHubPage = lazy(() => import('./pages/Fitness'));
const AvaliacoesFisicasPage = lazy(() => import('./pages/Fitness/Avaliacoes'));
const AvaliacaoFisicaDetailPage = lazy(() => import('./pages/Fitness/Avaliacoes/Detail'));
const AulasGrupoPage = lazy(() => import('./pages/Fitness/Aulas'));

// Lazy-loaded pages - Educação (setor educacao)
const EducacaoPage = lazy(() => import('./pages/Educacao'));
const FichaAlunoDetailPage = lazy(() => import('./pages/Educacao/FichasAluno'));

// Lazy-loaded pages - Casos/Jurídico (setor juridico)
const CasosPage = lazy(() => import('./pages/Casos'));
const CasoDetailPage = lazy(() => import('./pages/Casos/Detail'));

// Lazy-loaded pages - Booking flow (high priority)
const BookingServicesPage = lazy(() => import('./pages/Booking/Services'));
const SelectBarberPage = lazy(() => import('./pages/Booking/Barber'));
const DateTimePage = lazy(() => import('./pages/Booking/DateTime'));
const CustomerSelectionPage = lazy(() => import('./pages/Booking/Customer'));
const ConfirmAppointmentPage = lazy(() => import('./pages/Booking/Confirm'));

// Lazy-loaded pages - Dashboard (high priority for authenticated users)
const DashboardRouter = lazy(() => import('./pages/Dashboard/DashboardRouter'));
const ProfilePage = lazy(() => import('./pages/Dashboard/ProfilePage'));
const SubscriptionPage = lazy(() => import('./pages/Dashboard/SubscriptionPage'));
const ProfissionalSettings = lazy(() => import('./pages/Dashboard/ProfissionalDashboard/Settings'));
const FinanceiroReportProfissional = lazy(() => import('./pages/Dashboard/ProfissionalDashboard/FinanceiroReport'));
const FinanceiroReportEstabelecimento = lazy(() => import('./pages/Dashboard/EstabelecimentoDashboard/FinanceiroReport'));
const DesempenhoFinanceiro = lazy(() => import('./pages/Dashboard/EstabelecimentoDashboard/DesempenhoFinanceiro'));
const AvaliacoesEstabelecimentoPage = lazy(() => import('./pages/Dashboard/EstabelecimentoDashboard/Avaliacoes'));
const TicketsSuporteEstabelecimento = lazy(() => import('./pages/Dashboard/EstabelecimentoDashboard/TicketsSuporteEstabelecimento'));
const ComissoesPage = lazy(() => import('./pages/Comissoes'));
const LinksManagerPage = lazy(() => import('./pages/Dashboard/EstabelecimentoDashboard/DashboardV2/LinksManager'));
const CRMPanelPage = lazy(() => import('./pages/Dashboard/EstabelecimentoDashboard/CRMPanel'));
const PromotionsManagementPage = lazy(() => import('./pages/Dashboard/EstabelecimentoDashboard/PromotionsManagement'));

// Lazy-loaded pages - Onboarding & Invitations
const OnboardingWizard = lazy(() => import('./pages/OnboardingWizard'));
const EstabelecimentoOnboarding = lazy(() => import('./pages/Onboarding/EstabelecimentoOnboarding'));
const AcceptInvitation = lazy(() => import('./pages/Invitation/AcceptInvitation'));
const AceitarConvite = lazy(() => import('./pages/Profissionais/AceitarConvite'));
const AceitarConviteCliente = lazy(() => import('./pages/Clientes/AceitarConvite'));
const AtivarConta = lazy(() => import('./pages/AtivarConta'));

// Lazy-loaded pages - Admin (low priority, rarely accessed)
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const ComingSoon = lazy(() => import('./pages/Admin/ComingSoon'));
const UsuariosManagement = lazy(() => import('./pages/Admin/UsuariosManagement'));
const PlanosManagement = lazy(() => import('./pages/Admin/PlanosManagement'));
const AlertasManagement = lazy(() => import('./pages/Admin/AlertasManagement'));
const AnalyticsDashboard = lazy(() => import('./pages/Admin/AnalyticsDashboard'));
const FaturamentoReport = lazy(() => import('./pages/Admin/FaturamentoReport'));
const LogsManagement = lazy(() => import('./pages/Admin/LogsManagement'));
const MonitoringDashboard = lazy(() => import('./pages/Admin/MonitoringDashboard'));
const SegurancaManagement = lazy(() => import('./pages/Admin/SegurancaManagement'));
const DatabaseAdmin = lazy(() => import('./pages/Admin/DatabaseAdmin'));
const ApiIntegrations = lazy(() => import('./pages/Admin/ApiIntegrations'));
const SuporteManagement = lazy(() => import('./pages/Admin/SuporteManagement'));
const RelatoriosFinanceiros = lazy(() => import('./pages/Admin/RelatoriosFinanceiros'));
const ServicesManagement = lazy(() => import('./pages/Admin/ServicesManagement'));
const AppointmentsManagement = lazy(() => import('./pages/Admin/AppointmentsManagement'));
const BackupRecovery = lazy(() => import('./pages/Admin/BackupRecovery'));
const EstablishmentsManagement = lazy(() => import('./pages/Admin/EstablishmentsManagement'));
const ConfigurationsManagement = lazy(() => import('./pages/Admin/ConfigurationsManagement'));
const OrganizedConfigurations = lazy(() => import('./pages/Admin/OrganizedConfigurations'));
const MercadoPagoConfigPage = lazy(() => import('./pages/Admin/MercadoPagoConfigPage'));
const WhatsAppConfigPage = lazy(() => import('./pages/Admin/WhatsAppConfigPage'));
const EmailConfigPage = lazy(() => import('./pages/Admin/EmailConfigPage'));
const FacebookConfigPage = lazy(() => import('./pages/Admin/FacebookConfigPage'));

// Lazy-loaded pages - Frontdesk (medium priority)
const FrontdeskView = lazy(() => import('./pages/FrenteLoja'));
const FrontdeskDashboard = lazy(() => import('./pages/Frontdesk/FrontdeskDashboard'));
const FrontdeskTV = lazy(() => import('./pages/Frontdesk/FrontdeskTV'));

// Lazy-loaded pages - Other features (low priority)
const HomePage = lazy(() => import('./pages/Home'));
const BarberProfilePage = lazy(() => import('./pages/Barber/[id]'));
const AppointmentsHistoryPage = lazy(() => import('./pages/Appointments/History'));
const ReviewPage = lazy(() => import('./pages/Appointments/[id]/review'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const AboutPage = lazy(() => import('./pages/About'));
const LocationPage = lazy(() => import('./pages/Location'));
const PromotionsPage = lazy(() => import('./pages/Promotions'));
const FAQPage = lazy(() => import('./pages/FAQ'));
const ContactPage = lazy(() => import('./pages/Contact'));
const AvaliarPage = lazy(() => import('./pages/Avaliar'));
const LoyaltyPage = lazy(() => import('./pages/Loyalty'));
const NotificationsPage = lazy(() => import('./pages/Notifications'));
const PaymentMethodsPage = lazy(() => import('./pages/PaymentMethods'));
const PrivacyPage = lazy(() => import('./pages/Privacy'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccess'));
const DemoPage = lazy(() => import('./pages/Demo'));
const ClaimSetupPage = lazy(() => import('./pages/ClaimSetup'));
const EstabelecimentoPage = lazy(() => import('./pages/Estabelecimento'));
const ThemeDemo = lazy(() => import('./pages/ThemeDemo'));
const InstagramCallbackPage = lazy(() => import('./pages/Auth/InstagramCallback'));
const FacebookCallbackPage = lazy(() => import('./pages/Auth/FacebookCallback'));
const ContentPage = lazy(() => import('./pages/ContentPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenter'));
const CategoryArticlesPage = lazy(() => import('./pages/HelpCenter/CategoryArticles'));
const ArticleDetailPage = lazy(() => import('./pages/HelpCenter/ArticleDetail'));
const BlogPage = lazy(() => import('./pages/Blog'));
const BlogPostDetailPage = lazy(() => import('./pages/Blog/PostDetail'));
const SuccessCasesPage = lazy(() => import('./pages/SuccessCases'));
const SuccessCaseDetailPage = lazy(() => import('./pages/SuccessCases/CaseDetail'));
const SystemStatusPage = lazy(() => import('./pages/SystemStatus'));
const ApiDocsPage = lazy(() => import('./pages/ApiDocsPage'));
const PricingPage = lazy(() => import('./pages/Pricing/index'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicy'));

// Lazy-loaded guards
const AdminGuard = lazy(() => import('./components/AdminGuard'));
const PlanGuard = lazy(() => import('./components/PlanGuard'));

// Lazy-loaded pages - White-label settings
const WhiteLabelSettings = lazy(() => import('./pages/Dashboard/EstabelecimentoDashboard/WhiteLabelSettings'));

// BookingLayout com suporte a white-label
const BookingLayout = lazy(() => import('./pages/Booking/BookingLayout'));

// Redirect component: /agendar/:id → /booking/:id/services (links compartilháveis)
const AgendasRedirect: React.FC = () => {
  const { estabelecimentoId } = useParams<{ estabelecimentoId: string }>();
  return <Navigate to={`/booking/${estabelecimentoId}/services`} replace />;
};

// Wrapper para White-label Settings: obtém estabelecimentoId do AuthContext
const WhiteLabelSettingsWrapper: React.FC = () => {
  const { user } = useAuth();
  const estabelecimentoId = user?.estabelecimento?.id;
  if (!estabelecimentoId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-400">Estabelecimento não encontrado.</p>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <Suspense fallback={<PageLoader />}>
        <WhiteLabelSettings estabelecimentoId={estabelecimentoId} />
      </Suspense>
    </div>
  );
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <Suspense fallback={<PageLoader />}><LandingPage /></Suspense> },
      { path: 'home', element: <Suspense fallback={<PageLoader />}><HomePage /></Suspense> },
      { path: 'buscar', element: <Suspense fallback={<PageLoader />}><BuscarPage /></Suspense> },
      { path: 'prontuarios', element: <Suspense fallback={<PageLoader />}><ProntuariosPage /></Suspense> },
      { path: 'prontuarios/:id', element: <Suspense fallback={<PageLoader />}><ProntuarioDetailPage /></Suspense> },
      // Fichas de Beleza (setor beleza)
      { path: 'fichas-beleza', element: <Suspense fallback={<PageLoader />}><FichasBelezaPage /></Suspense> },
      { path: 'fichas-beleza/:id', element: <Suspense fallback={<PageLoader />}><FichaBelezaDetailPage /></Suspense> },
      // Fitness (setor fitness)
      { path: 'fitness', element: <Suspense fallback={<PageLoader />}><FitnessHubPage /></Suspense> },
      { path: 'fitness/avaliacoes', element: <Suspense fallback={<PageLoader />}><AvaliacoesFisicasPage /></Suspense> },
      { path: 'fitness/avaliacoes/:id', element: <Suspense fallback={<PageLoader />}><AvaliacaoFisicaDetailPage /></Suspense> },
      { path: 'fitness/aulas', element: <Suspense fallback={<PageLoader />}><AulasGrupoPage /></Suspense> },
      // Educação (setor educacao)
      { path: 'educacao', element: <Suspense fallback={<PageLoader />}><EducacaoPage /></Suspense> },
      { path: 'educacao/fichas/:id', element: <Suspense fallback={<PageLoader />}><FichaAlunoDetailPage /></Suspense> },
      // Casos / Jurídico (setor juridico)
      { path: 'casos', element: <Suspense fallback={<PageLoader />}><CasosPage /></Suspense> },
      { path: 'casos/:id', element: <Suspense fallback={<PageLoader />}><CasoDetailPage /></Suspense> },
      { path: 'login', element: <Suspense fallback={<PageLoader />}><LoginPage /></Suspense> },
      { path: 'register', element: <Suspense fallback={<PageLoader />}><RegisterPage /></Suspense> },
      { path: 'demo', element: <Suspense fallback={<PageLoader />}><DemoPage /></Suspense> },
      { path: 'para/:setor', element: <Suspense fallback={<PageLoader />}><SectorLanding /></Suspense> },
      { path: 'forgot-password', element: <Suspense fallback={<PageLoader />}><ForgotPasswordPage /></Suspense> },
      { path: 'reset-password', element: <Suspense fallback={<PageLoader />}><ResetPasswordPage /></Suspense> },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <OnboardingMiddleware>
              <Suspense fallback={<PageLoader />}>
                <DashboardRouter />
              </Suspense>
            </OnboardingMiddleware>
          </ProtectedRoute>
        )
      },
      {
        path: 'onboarding',
        element: (
          <BusinessOwnerProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <EstabelecimentoOnboarding />
            </Suspense>
          </BusinessOwnerProtectedRoute>
        )
      },
      {
        // Wizard interativo de configuração inicial (5 passos)
        path: 'onboarding/wizard',
        element: (
          <BusinessOwnerProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <OnboardingWizard />
            </Suspense>
          </BusinessOwnerProtectedRoute>
        )
      },
      { path: '/convite/aceitar', element: <Suspense fallback={<PageLoader />}><AcceptInvitation /></Suspense> },
      { path: '/profissionais/aceitar-convite', element: <Suspense fallback={<PageLoader />}><AceitarConvite /></Suspense> },
      { path: '/clientes/aceitar-convite', element: <Suspense fallback={<PageLoader />}><AceitarConviteCliente /></Suspense> },
      { path: '/ativar-conta/:token', element: <Suspense fallback={<PageLoader />}><AtivarConta /></Suspense> },
      {
        path: 'dashboard/profile',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <ProfilePage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        path: 'dashboard/subscription',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <SubscriptionPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        path: 'dashboard/financeiro-estabelecimento',
        element: (
          <BusinessOwnerProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <FinanceiroReportEstabelecimento />
            </Suspense>
          </BusinessOwnerProtectedRoute>
        )
      },
      {
        path: 'dashboard/desempenho',
        element: (
          <BusinessOwnerProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <DesempenhoFinanceiro />
            </Suspense>
          </BusinessOwnerProtectedRoute>
        )
      },
      {
        path: 'dashboard/suporte',
        element: (
          <BusinessOwnerProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <TicketsSuporteEstabelecimento />
            </Suspense>
          </BusinessOwnerProtectedRoute>
        )
      },
      {
        path: 'dashboard/comissoes',
        element: (
          <BusinessOwnerProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <ComissoesPage />
            </Suspense>
          </BusinessOwnerProtectedRoute>
        )
      },
      {
        path: 'dashboard/links',
        element: (
          <BusinessOwnerProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <LinksManagerPage />
            </Suspense>
          </BusinessOwnerProtectedRoute>
        )
      },
      {
        path: 'dashboard/crm',
        element: (
          <BusinessOwnerProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <CRMPanelPage />
            </Suspense>
          </BusinessOwnerProtectedRoute>
        )
      },
      {
        path: 'dashboard/promocoes',
        element: (
          <BusinessOwnerProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <PromotionsManagementPage />
            </Suspense>
          </BusinessOwnerProtectedRoute>
        )
      },
      // White-label route removida — funcionalidade em desenvolvimento
      // More routes with lazy loading...
      // Redirect /agendar/:id → /booking/:id/services (links compartilháveis)
      { path: 'agendar/:estabelecimentoId', element: <AgendasRedirect /> },
      // Booking flow com white-label (BookingLayout como layout pai)
      {
        path: 'booking/:estabelecimentoId',
        element: <Suspense fallback={<PageLoader />}><BookingLayout /></Suspense>,
        children: [
          { path: 'services', element: <Suspense fallback={<PageLoader />}><BookingServicesPage /></Suspense> },
          { path: 'barber', element: <Suspense fallback={<PageLoader />}><SelectBarberPage /></Suspense> },
          { path: 'datetime', element: <Suspense fallback={<PageLoader />}><DateTimePage /></Suspense> },
          { path: 'customer', element: (
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}><CustomerSelectionPage /></Suspense>
            </ProtectedRoute>
          ) },
          { path: 'confirm', element: <Suspense fallback={<PageLoader />}><ConfirmAppointmentPage /></Suspense> },
        ],
      },
      { path: 'about', element: <Suspense fallback={<PageLoader />}><AboutPage /></Suspense> },
      { path: 'faq', element: <Suspense fallback={<PageLoader />}><FAQPage /></Suspense> },
      { path: 'contact', element: <Suspense fallback={<PageLoader />}><ContactPage /></Suspense> },
      { path: 'pricing', element: <Suspense fallback={<PageLoader />}><PricingPage /></Suspense> },
      { path: 'p/termos-de-uso', element: <Suspense fallback={<PageLoader />}><TermsOfServicePage /></Suspense> },
      { path: 'p/politica-de-privacidade', element: <Suspense fallback={<PageLoader />}><PrivacyPolicyPage /></Suspense> },
      { path: 'privacy-policy', element: <Suspense fallback={<PageLoader />}><PrivacyPolicyPage /></Suspense> },
      { path: 'terms-of-service', element: <Suspense fallback={<PageLoader />}><TermsOfServicePage /></Suspense> },
      {
        path: 'settings',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <SettingsPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        path: 'appointments/history',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <AppointmentsHistoryPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        path: 'frontdesk/tv',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <FrontdeskTV />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        path: 'appointments/:id/review',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <ReviewPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        path: 'avaliar/:agendamentoId',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <AvaliarPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      {
        path: 'loyalty',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <LoyaltyPage />
            </Suspense>
          </ProtectedRoute>
        )
      },
      // Admin Routes — todas as rotas /admin/* compartilham o AdminLayout (sidebar persistente)
      {
        path: 'admin',
        element: (
          <ProtectedRoute>
            <Suspense fallback={<PageLoader />}>
              <AdminLayout />
            </Suspense>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/admin/analytics" replace />
          },
          {
            path: 'analytics',
            element: <Suspense fallback={<PageLoader />}><AnalyticsDashboard /></Suspense>
          },
          {
            path: 'usuarios',
            element: <Suspense fallback={<PageLoader />}><UsuariosManagement /></Suspense>
          },
          {
            path: 'suporte',
            element: <Suspense fallback={<PageLoader />}><SuporteManagement /></Suspense>
          },
          {
            path: 'planos',
            element: <Suspense fallback={<PageLoader />}><PlanosManagement /></Suspense>
          },
          {
            path: 'faturamento',
            element: <Suspense fallback={<PageLoader />}><FaturamentoReport /></Suspense>
          },
          {
            path: 'relatorios',
            element: <Suspense fallback={<PageLoader />}><RelatoriosFinanceiros /></Suspense>
          },
          {
            path: 'logs',
            element: <Suspense fallback={<PageLoader />}><LogsManagement /></Suspense>
          },
          {
            path: 'monitoring',
            element: <Suspense fallback={<PageLoader />}><MonitoringDashboard /></Suspense>
          },
          {
            path: 'alertas',
            element: <Suspense fallback={<PageLoader />}><AlertasManagement /></Suspense>
          },
          {
            path: 'database',
            element: <Suspense fallback={<PageLoader />}><DatabaseAdmin /></Suspense>
          },
          {
            path: 'api',
            element: <Suspense fallback={<PageLoader />}><ApiIntegrations /></Suspense>
          },
          {
            path: 'mercadopago',
            element: <Suspense fallback={<PageLoader />}><MercadoPagoConfigPage /></Suspense>
          },
          {
            path: 'seguranca',
            element: <Suspense fallback={<PageLoader />}><SegurancaManagement /></Suspense>
          },
          {
            path: 'estabelecimento',
            element: <Suspense fallback={<PageLoader />}><EstablishmentsManagement /></Suspense>
          },
          {
            path: 'agendamentos',
            element: <Suspense fallback={<PageLoader />}><AppointmentsManagement /></Suspense>
          },
          {
            path: 'servicos',
            element: <Suspense fallback={<PageLoader />}><ServicesManagement /></Suspense>
          },
          {
            path: 'configuracoes',
            element: <Suspense fallback={<PageLoader />}><OrganizedConfigurations /></Suspense>
          },
          {
            path: 'backup',
            element: <Suspense fallback={<PageLoader />}><BackupRecovery /></Suspense>
          },
        ]
      },
    ]
  }
]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <RouterProvider router={router} />
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
